import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Car, ArrowLeft, MapPinned, Ban, Navigation2 } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { SeoFooter } from "@/components/SeoFooter";
import { FaqSection } from "@/components/FaqSection";
import { useSeo, useJsonLd, ogImageUrl } from "@/lib/seo";
import { findDepartment, DEPARTMENTS, DEPT_PATH } from "@/lib/departments";
import { departmentFaq } from "@/lib/faq";
import { findCity, CITY_PATH } from "@/lib/cities";
import { THEMATIC_PAGES } from "@/lib/thematic";

export default function DepartmentPage() {
  const { slug } = useParams();
  const dept = findDepartment(slug);
  const faqItems = dept ? departmentFaq(dept) : [];

  useSeo(
    dept ? `GPS voiture sans permis ${dept.prep} (${dept.code}) — 45 km/h | vspo` : "vspo",
    dept
      ? `Itinéraires pour voiture sans permis (VSP) ${dept.prep} (${dept.code}) avec vspo : trajets 45 km/h évitant autoroutes et voies rapides, navigation vocale et trafic en temps réel.`
      : "",
    dept ? `${DEPT_PATH}/${dept.slug}` : undefined,
    dept ? ogImageUrl({ title: dept.name, subtitle: `Voiture sans permis (${dept.code})` }) : undefined
  );

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  useJsonLd(
    "dept-jsonld",
    dept
      ? [
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Accueil", item: `${origin}/` },
              { "@type": "ListItem", position: 2, name: `VSP ${dept.prep}`, item: `${origin}${DEPT_PATH}/${dept.slug}` },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "WebApplication",
            name: `vspo — GPS voiture sans permis ${dept.prep}`,
            url: `${origin}${DEPT_PATH}/${dept.slug}`,
            applicationCategory: "TravelApplication",
            operatingSystem: "Web",
            inLanguage: "fr-FR",
            offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
            areaServed: { "@type": "AdministrativeArea", name: `${dept.name} (${dept.code})` },
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqItems.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ]
      : null
  );

  if (!dept) return <Navigate to="/" replace />;

  const cityObjs = dept.cities.map((s) => findCity(s)).filter(Boolean);
  const others = DEPARTMENTS.filter((d) => d.slug !== dept.slug);

  return (
    <div className="min-h-screen w-full overflow-y-auto bg-background">
      <header className="sticky top-0 z-10 border-b border-border bg-card/95 backdrop-blur-md">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground">
              <Car className="h-5 w-5" />
            </span>
            <div className="leading-tight">
              <div className="font-display text-lg font-bold text-foreground">vspo</div>
              <div className="text-[11px] text-muted-foreground">GPS voiture sans permis · 45 km/h</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <ThemeToggle />
            <Link to="/" className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground" data-testid="dept-open-app">
              <ArrowLeft className="h-4 w-4" /> Ouvrir le GPS
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 pb-16 pt-6">
        <nav className="mb-3 text-xs text-muted-foreground" aria-label="Fil d'ariane">
          <Link to="/" className="hover:text-primary">Accueil</Link> ·{" "}
          <span className="text-foreground">{dept.name}</span>
        </nav>

        <h1 className="font-display text-3xl font-bold text-foreground" data-testid="dept-title">
          GPS voiture sans permis {dept.prep}
        </h1>
        <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
          Vous roulez en <strong>voiture sans permis {dept.prep}</strong> ({dept.code}) ? <strong>vspo</strong>
          {" "}calcule des itinéraires adaptés aux voiturettes bridées à <strong>45 km/h</strong>, qui évitent
          automatiquement les autoroutes et les voies rapides interdites aux VSP.
        </p>

        <section className="mt-8">
          <div className="mb-3 flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary"><Ban className="h-5 w-5" /></span>
            <h2 className="font-display text-xl font-bold text-foreground">Rouler en VSP {dept.prep}</h2>
          </div>
          <p className="text-[15px] leading-relaxed text-muted-foreground">
            {dept.prep.charAt(0).toUpperCase() + dept.prep.slice(1)}, les voies rapides, rocades et voies pour
            automobiles sont interdites aux voitures sans permis. vspo trace un trajet sur le réseau ordinaire,
            à la vitesse réelle d'une voiturette, avec navigation vocale et trafic en temps réel.
          </p>
        </section>

        {cityObjs.length > 0 && (
          <section className="mt-8">
            <div className="mb-3 flex items-center gap-2">
              <MapPinned className="h-5 w-5 text-primary" />
              <h2 className="font-display text-xl font-bold text-foreground">Villes {dept.prep}</h2>
            </div>
            <div className="flex flex-wrap gap-2">
              {cityObjs.map((c) => (
                <Link key={c.slug} to={`${CITY_PATH}/${c.slug}`} className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-primary" data-testid={`dept-city-${c.slug}`}>
                  {c.name}
                </Link>
              ))}
            </div>
          </section>
        )}

        <div className="mt-10 rounded-2xl border border-border bg-card p-5">
          <h2 className="font-display text-lg font-bold text-foreground">Calculez votre itinéraire {dept.prep}</h2>
          <p className="mt-1 text-sm text-muted-foreground">Ouvrez vspo et entrez votre départ et votre arrivée pour un trajet adapté à votre voiture sans permis.</p>
          <Link to="/" className="mt-3 inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground" data-testid="dept-cta">
            <Navigation2 className="h-4 w-4" /> Ouvrir le GPS
          </Link>
        </div>

        <section className="mt-8">
          <div className="flex flex-wrap gap-2">
            {THEMATIC_PAGES.map((t) => (
              <Link key={t.slug} to={t.path} className="text-sm text-primary hover:underline" data-testid={`dept-thematic-${t.slug}`}>{t.kicker}</Link>
            ))}
          </div>
        </section>

        <section className="mt-10">
          <h2 className="font-display text-base font-bold text-foreground">Autres départements</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {others.map((d) => (
              <Link key={d.slug} to={`${DEPT_PATH}/${d.slug}`} className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-primary" data-testid={`dept-link-${d.slug}`}>
                {d.name}
              </Link>
            ))}
          </div>
        </section>

        <FaqSection items={faqItems} />
      </main>

      <SeoFooter />
    </div>
  );
}
